/**
 * answerDedupeNormalize.ts — ONE normalization for "is this the same answer?"
 * (client twin of functions/src/prest0nSession/answerDedupeNormalize.ts).
 *
 * INCIDENT-20260901 (Acme NDA thread, owner screenshot): the server appended
 * a code-authored version-fact line ("_updated to version 2 — …_") to its
 * durable copy of a document answer. The duplicate-answer belts — the
 * server's array-merge text belt and this client's adjacent / turn-window
 * passes (interruptedTurn.ts) — compare BYTE-EQUAL normalized text, so the
 * one line turned the server copy into "a different answer" from the live
 * copy and a second bubble rendered.
 *
 * Rule going forward: answer text is never mutated server-side for a
 * presentational fact; the fact rides structured fields (attachments carry
 * docVersion) and the client renders the line (AttachmentsSection). This
 * normalizer covers rows already persisted with the line — they converge on
 * read and the duplicated threads self-heal — and defines "trailing
 * code-authored suffix" for every belt.
 *
 * LOCKSTEP with the functions twin: regex sources pinned equal by
 * functions/src/__tests__/answerDedupeNormalize.lockstep.test.ts.
 */

export const LEGACY_DOC_VERSION_FACT_SUFFIX_RE =
  /\s*_?(?:updated to version \d+ — this replaces the earlier version of this document\.|actualizado a la versión \d+ — reemplaza la versión anterior de este documento\.)_?\s*$/u;

export function normalizeAnswerTextForDedupe(
  text: unknown,
  extraSuffixes: readonly string[] = [],
): string {
  let body = String(text ?? '');
  for (let pass = 0; pass < 4; pass += 1) {
    const before = body;
    body = body.replace(LEGACY_DOC_VERSION_FACT_SUFFIX_RE, '');
    for (const suffix of extraSuffixes) {
      const trimmed = body.trimEnd();
      if (suffix && trimmed.endsWith(suffix)) {
        body = trimmed.slice(0, trimmed.length - suffix.length);
      }
    }
    if (body === before) break;
  }
  return body.replace(/\s+/g, ' ').trim();
}

/** True when the body already carries the legacy server-appended fact line. */
export function hasLegacyDocVersionFactLine(text: unknown): boolean {
  return LEGACY_DOC_VERSION_FACT_SUFFIX_RE.test(String(text ?? ''));
}
